import { createBaseStore } from '@tasama/utils';
import {
  validateNewTicketUploadForm,
  type NewTicketUploadFormData,
  newTicketUploadDefaultValues,
} from '../schemas/new-ticket-upload.schema';

/**
 * New Ticket Upload store — file upload manager
 *
 * This store manages file upload state for the New Ticket drawer.
 * It handles upload progress, errors, and the file list.
 * Files are optional for ticket creation (minFiles: 0).
 */

export interface NewTicketUploadedFile {
  id: string;
  file?: File;
  fileName: string;
  fileSize: string;
  bytes?: number | undefined;
  status: 'uploading' | 'complete' | 'error' | 'pending';
  progress: number;
  uploadUrl?: string;
  errorMessage?: string;
}

interface NewTicketUploadState {
  // Form data
  formData: NewTicketUploadFormData;
  errors: Record<string, string>;

  // Upload state
  uploadedFiles: NewTicketUploadedFile[];
  uploadError: string | null;
  isUploading: boolean;
  uploadProgress: number;

  // Loading and error states
  loading: boolean;
  error: string | null;

  // Actions
  addFiles: (_files: NewTicketUploadedFile[]) => void;
  addUploadedFile: (_file: NewTicketUploadedFile) => void;
  removeUploadedFile: (_fileId: string) => void;
  removeFile: (_fileId: string) => void;
  updateFile: (
    _fileId: string,
    _updates: Partial<NewTicketUploadedFile>
  ) => void;
  updateFileStatus: (
    _fileId: string,
    _status: NewTicketUploadedFile['status'],
    _progress?: number,
    _errorMessage?: string
  ) => void;
  setUploadError: (_error: string | null) => void;
  clearUploadError: () => void;
  setIsUploading: (_uploading: boolean) => void;
  /** Alias for setIsUploading — satisfies the useFileUploadSubmit contract. */
  setLoading: (_loading: boolean) => void;
  setUploadProgress: (_progress: number) => void;
  validateForm: () => boolean;
  /** Checks that files are valid (optional for tickets, so always returns success if empty). */
  validateFiles: () => { success: boolean; error?: string };
  /** Builds FormData from uploaded files. */
  getFormData: () => FormData | null;
  resetUploadState: () => void;
}

const initialState: any = {
  formData: newTicketUploadDefaultValues,
  errors: {},
  uploadedFiles: [],
  uploadError: null,
  isUploading: false,
  uploadProgress: 0,
};

export const useNewTicketUploadStore = createBaseStore<NewTicketUploadState>(
  initialState,
  (set, get) => ({
    // Actions
    addFiles: (files: NewTicketUploadedFile[]) => {
      set((state: NewTicketUploadState) => {
        const newFiles = [...state.uploadedFiles, ...files];
        return {
          uploadedFiles: newFiles,
          formData: {
            ...state.formData,
            uploadedFiles: newFiles.map((f: NewTicketUploadedFile) => ({
              id: f.id,
              fileName: f.fileName,
              fileSize: f.fileSize,
              bytes: f.bytes,
              progress: f.progress,
              status: f.status as any,
            })),
          },
        };
      });
    },

    addUploadedFile: (file: NewTicketUploadedFile) => {
      set((state: NewTicketUploadState) => {
        const newFiles = [...state.uploadedFiles, file];
        return {
          uploadedFiles: newFiles,
          formData: {
            ...state.formData,
            uploadedFiles: newFiles.map((f: NewTicketUploadedFile) => ({
              id: f.id,
              fileName: f.fileName,
              fileSize: f.fileSize,
              bytes: f.bytes,
              progress: f.progress,
              status: f.status as any,
            })),
          },
        };
      });
    },

    removeUploadedFile: (fileId: string) => {
      set((state: NewTicketUploadState) => {
        const newFiles = state.uploadedFiles.filter(
          (file: NewTicketUploadedFile) => file.id !== fileId
        );
        return {
          uploadedFiles: newFiles,
          formData: {
            ...state.formData,
            uploadedFiles: newFiles.map((f: NewTicketUploadedFile) => ({
              id: f.id,
              fileName: f.fileName,
              fileSize: f.fileSize,
              bytes: f.bytes,
              progress: f.progress,
              status: f.status as any,
            })),
          },
        };
      });
    },

    removeFile: (fileId: string) => {
      get().removeUploadedFile(fileId);
    },

    updateFile: (fileId: string, updates: Partial<NewTicketUploadedFile>) => {
      set((state: NewTicketUploadState) => {
        const newFiles = state.uploadedFiles.map(
          (file: NewTicketUploadedFile) =>
            file.id === fileId ? { ...file, ...updates } : file
        );
        return {
          uploadedFiles: newFiles,
          formData: {
            ...state.formData,
            uploadedFiles: newFiles.map((f: NewTicketUploadedFile) => ({
              id: f.id,
              fileName: f.fileName,
              fileSize: f.fileSize,
              bytes: f.bytes,
              progress: f.progress,
              status: f.status as any,
            })),
          },
        };
      });
    },

    updateFileStatus: (
      fileId: string,
      status: NewTicketUploadedFile['status'],
      progress = 0,
      errorMessage?: string
    ) => {
      get().updateFile(fileId, { status, progress, errorMessage });
    },

    setUploadError: (error: string | null) => {
      set({
        uploadError: error,
        errors: error ? { uploadedFiles: error } : {},
      });
    },

    clearUploadError: () => {
      set({ uploadError: null, errors: {} });
    },

    setIsUploading: (uploading: boolean) => {
      set({ isUploading: uploading });
    },

    setLoading: (loading: boolean) => {
      set({ isUploading: loading });
    },

    setUploadProgress: (progress: number) => {
      set({ uploadProgress: progress });
    },

    validateForm: () => {
      const state = get();
      const result = validateNewTicketUploadForm(state.formData);

      if (result.success) {
        set({ errors: {} });
        return true;
      } else {
        set({ errors: result.errors || {} });
        return false;
      }
    },

    validateFiles: () => {
      const state = get();
      const result = validateNewTicketUploadForm(state.formData);

      if (!result.success) {
        // Return the exact error from schema validation
        const errorMessage =
          result.errors?.uploadedFiles || 'File validation failed';
        return { success: false, error: errorMessage };
      }

      return { success: true };
    },

    getFormData: () => {
      const { uploadedFiles } = get();
      if (uploadedFiles.length === 0) return null;

      const formData = new FormData();
      uploadedFiles.forEach((uploadedFile: NewTicketUploadedFile) => {
        if (uploadedFile.file) {
          formData.append('files', uploadedFile.file);
        }
      });
      return formData;
    },

    resetUploadState: () => {
      set(initialState);
    },
  })
);

// Export store selectors for easy access
export const useNewTicketUploadFormData = () =>
  useNewTicketUploadStore((state) => state.formData);
export const useNewTicketUploadErrors = () =>
  useNewTicketUploadStore((state) => state.errors);
export const useNewTicketUploadedFiles = () =>
  useNewTicketUploadStore((state) => state.formData.uploadedFiles);
export const useNewTicketUploadError = () =>
  useNewTicketUploadStore((state) => state.uploadError);
export const useNewTicketIsUploading = () =>
  useNewTicketUploadStore((state) => state.isUploading);
export const useNewTicketUploadProgress = () =>
  useNewTicketUploadStore((state) => state.uploadProgress);
